import React from "react";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocCard,
  DocAlert,
  DocStepIndicator,
  DocCodeBlock,
  DocNavFooter,
  DocLink,
} from "@/components/docs";
import { docsColors, docsComponents } from "@/lib/docs-theme";

export default function GettingStartedPage() {
  const breadcrumbs = [{ label: "Documentation", href: "/docs" }, { label: "Getting Started" }];

  return (
    <DocLayout
      title="Getting Started - Uptime Monitor Documentation"
      description="Get started with Uptime Monitor in minutes. Create your first monitor and start receiving alerts."
      canonical="https://uptime-monitor.io/docs/getting-started"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Getting Started"
        description="Follow these simple steps to create your first monitor and start receiving alerts in under 5 minutes."
      />

      {/* Steps */}
      <div className="space-y-8">
        {/* Step 1: Sign Up */}
        <DocCard>
          <div className="flex items-start">
            <DocStepIndicator number={1} size="large" />
            <div className="ml-4 flex-1">
              <h2 className={docsComponents.sectionHeading}>Create Your Account</h2>
              <p className={docsComponents.sectionDescription}>
                Create your account at{" "}
                <DocLink href="https://uptime-monitor.io/accounts/login" external>
                  uptime-monitor.io/accounts/login
                </DocLink>
                . No credit card required to start your free trial.
              </p>
              <ul className={`list-disc list-inside ${docsColors.text.body} space-y-1 text-sm`}>
                <li>Enter your email address</li>
                <li>Check your email for a one-time login code</li>
                <li>Enter the code to access your dashboard</li>
                <li>Choose a plan to start your free trial</li>
              </ul>
            </div>
          </div>
        </DocCard>

        {/* Step 2: Create Your First Monitor */}
        <DocCard>
          <div className="flex items-start">
            <DocStepIndicator number={2} size="large" />
            <div className="ml-4 flex-1">
              <h2 className={docsComponents.sectionHeading}>Create Your First Monitor</h2>
              <p className={docsComponents.sectionDescription}>
                After logging in, click the &quot;Add New Monitor&quot; button to create your first
                monitor.
              </p>

              <DocCodeBlock className="mb-4">
                <h3 className="font-semibold mb-2">Basic Settings:</h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    <span className="font-medium">Monitor Name:</span> Give your monitor a
                    descriptive name (e.g., &quot;Production Website&quot;)
                  </li>
                  <li>
                    <span className="font-medium">URL:</span> Enter the URL you want to monitor
                    (e.g., https://example.com)
                  </li>
                  <li>
                    <span className="font-medium">Check Frequency:</span> Select how often to check
                    (30 seconds to 60 minutes)
                  </li>
                  <li>
                    <span className="font-medium">Monitor Type:</span> Choose HTTP/HTTPS for
                    websites
                  </li>
                </ul>
              </DocCodeBlock>

              <p className={`${docsColors.text.body} text-sm`}>
                Leave other settings at their defaults for now. You can always adjust them later.
              </p>
            </div>
          </div>
        </DocCard>

        {/* Step 3: Configure Notifications */}
        <DocCard>
          <div className="flex items-start">
            <DocStepIndicator number={3} size="large" />
            <div className="ml-4 flex-1">
              <h2 className={docsComponents.sectionHeading}>Set Up Notifications</h2>
              <p className={docsComponents.sectionDescription}>
                Configure how you want to be notified when your monitor detects an issue.
              </p>

              <DocCodeBlock className="mb-4">
                <h3 className="font-semibold mb-2">Email Notifications (Default):</h3>
                <ul className="space-y-2 text-sm">
                  <li>Your account email is automatically added as a contact</li>
                  <li>Your daily email limit matches your plan&apos;s monitor count</li>
                  <li>Additional email credit packages available (1,000 / 3,000 / 10,000)</li>
                </ul>
              </DocCodeBlock>

              <DocCodeBlock>
                <h3 className="font-semibold mb-2">Other Notification Options:</h3>
                <ul className="space-y-1 text-sm">
                  <li>• Webhook notifications</li>
                  <li>• PagerDuty integration</li>
                  <li>• Slack and Discord</li>
                </ul>
              </DocCodeBlock>
            </div>
          </div>
        </DocCard>

        {/* Step 4: Save and Test */}
        <DocCard>
          <div className="flex items-start">
            <DocStepIndicator number={4} size="large" />
            <div className="ml-4 flex-1">
              <h2 className={docsComponents.sectionHeading}>Save and Verify</h2>
              <p className={docsComponents.sectionDescription}>
                Click &quot;Create Monitor&quot; to save your settings. Your monitor will start
                checking immediately.
              </p>

              <DocAlert variant="info" className="mb-4">
                <p className="text-sm">
                  <strong>Testing notifications:</strong> Notifications are sent when your monitor
                  detects a real status change. To test, you can temporarily point your monitor to
                  an invalid URL to trigger a down alert, then fix it to receive a recovery
                  notification.
                </p>
              </DocAlert>

              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                Understanding Monitor States:
              </h3>
              <ul className="space-y-2 text-sm">
                <li className={docsColors.text.body}>
                  <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  <span className={`font-medium ${docsColors.text.heading}`}>Online:</span> Service
                  is responding correctly
                </li>
                <li className={docsColors.text.body}>
                  <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                  <span className={`font-medium ${docsColors.text.heading}`}>Offline:</span> Service
                  is not responding or returning errors
                </li>
                <li className={docsColors.text.body}>
                  <span className="inline-block w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                  <span className={`font-medium ${docsColors.text.heading}`}>Pending:</span> Waiting
                  for first check result
                </li>
              </ul>
            </div>
          </div>
        </DocCard>

        {/* What's Next */}
        <DocAlert variant="info" className="p-6">
          <h2 className="text-xl font-semibold mb-4">🎉 Congratulations!</h2>
          <p className="mb-4">
            You&apos;ve successfully created your first monitor. It&apos;s now actively checking
            your service and will alert you if any issues are detected.
          </p>

          <h3 className="font-semibold mb-3">What&apos;s Next?</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <DocLink href="/docs/monitors/http" className="flex items-center">
              <span>Learn about advanced HTTP monitoring</span>
              <ArrowRightIcon className="h-4 w-4 ml-1" />
            </DocLink>
            <DocLink href="/docs/notifications" className="flex items-center">
              <span>Configure additional notification channels</span>
              <ArrowRightIcon className="h-4 w-4 ml-1" />
            </DocLink>
            <DocLink href="/docs/status-pages" className="flex items-center">
              <span>Create a public status page</span>
              <ArrowRightIcon className="h-4 w-4 ml-1" />
            </DocLink>
            <DocLink href="/docs/best-practices" className="flex items-center">
              <span>Read monitoring best practices</span>
              <ArrowRightIcon className="h-4 w-4 ml-1" />
            </DocLink>
          </div>
        </DocAlert>
      </div>

      <DocNavFooter
        prev={{ href: "/docs", text: "Documentation Home" }}
        next={{ href: "/docs/monitors", text: "Monitor Types" }}
        className="mt-12"
      />
    </DocLayout>
  );
}
